import Layout from "@/components/feature/Layout";
import PageHero from "@/components/base/PageHero";
import SectionHeader from "@/components/base/SectionHeader";
import CTAPanel from "@/components/base/CTAPanel";
import RelatedContent from "@/components/feature/RelatedContent";
import { Link } from "react-router-dom";

const categories = [
  {
    title: "USA Government Experience",
    description: "Technology delivery across U.S. federal, state, and local government environments — enterprise infrastructure, databases, Microsoft platforms, application integration, and public-sector digital solutions.",
    icon: "ri-government-line",
    href: "/experience/usa-government",
    tags: ["Federal", "State & Local", "Enterprise IT"],
  },
  {
    title: "International Government Experience",
    description: "India-based government and enterprise technology projects spanning custom applications, digital platforms, and systems integration.",
    icon: "ri-global-line",
    href: "/experience/international",
    tags: ["India", "Applications", "Systems Integration"],
  },
  {
    title: "Commercial Enterprise",
    description: "Technology delivery within commercial enterprise environments across applications, databases, platforms, and integration.",
    icon: "ri-building-3-line",
    href: "/experience/commercial",
    tags: ["Enterprise", "Platforms", "Delivery"],
  },
];

const missionConnection = [
  {
    title: "Application & Platform Engineering",
    items: [
      "Enterprise application development",
      "Application modernization",
      "Application integration",
      "Application support",
    ],
  },
  {
    title: "Web & Digital Solutions",
    items: [
      "Web applications",
      "Web portals",
      "Website / CMS solutions",
      "Digital modernization",
    ],
  },
  {
    title: "Database & Data Platform Services",
    items: [
      "Database engineering",
      "Database administration",
      "SQL-based systems",
      "Database-backed applications",
    ],
  },
  {
    title: "Microsoft Solutions",
    items: [
      "SharePoint",
      "Power Apps",
      "Power Automate",
      "Power BI",
      "Workflow solutions",
    ],
  },
  {
    title: "Enterprise Infrastructure & Network Engineering",
    items: [
      "LAN / WAN",
      "Server / platform support",
      "VMware",
      "Virtualization",
      "Infrastructure modernization",
    ],
  },
  {
    title: "Systems Integration",
    items: [
      "Application integration",
      "Enterprise-system integration",
      "Workflow integration",
      "ERP-related integration",
    ],
  },
  {
    title: "Technical Delivery",
    items: [
      "Technical SMEs",
      "Project delivery teams",
      "Implementation",
      "Testing",
      "Training",
      "Production support",
    ],
  },
];

const summary = [
  {
    label: "USA Government",
    detail: "Federal, state and local technology delivery",
    icon: "ri-government-line",
  },
  {
    label: "International Government",
    detail: "India-based government and enterprise technology projects",
    icon: "ri-global-line",
  },
  {
    label: "Commercial",
    detail: "Enterprise technology environments",
    icon: "ri-building-3-line",
  },
];

export default function ExperienceOverview() {
  return (
    <Layout>
      <PageHero
        title="Proven Experience Across Government & Enterprise Technology"
        subtitle="Experience"
        description="Mitaja Corporation brings hands-on technology delivery experience across U.S. federal, state and local, international, and commercial environments. Our experience spans infrastructure engineering, application development, enterprise platforms, database systems, systems integration, digital solutions and specialized technical services."
        size="lg"
      />

      <section className="section-padding bg-white">
        <div className="container-page">
          <SectionHeader
            title="Experience by Category"
            description="Explore Mitaja's documented technology delivery experience across public and private sectors."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {categories.map((category) => (
              <Link
                key={category.title}
                to={category.href}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="card-base card-hover p-6 md:p-8 cursor-pointer group block"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                    <i className={`${category.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-300 group-hover:text-primary-600 group-hover:translate-x-1 transition-all"></i>
                </div>
                <h3 className="text-xl font-bold text-foreground-950 mb-2 group-hover:text-primary-700 transition-colors">
                  {category.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {category.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {category.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-secondary-50 text-secondary-700 font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-background-100">
        <div className="container-page">
          <SectionHeader
            title="Experience That Supports Future Missions"
            description="Verified project experience connects directly to the core capabilities Mitaja delivers for government and enterprise programs."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {missionConnection.map((cap) => (
              <div key={cap.title} className="card-base p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-md bg-accent-50 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-accent-600"></i>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950">
                    {cap.title}
                  </h3>
                </div>
                <ul className="space-y-2.5">
                  {cap.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-foreground-600 leading-snug">
                      <i className="ri-arrow-right-s-line text-primary-500 mt-0.5 shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary-900">
        <div className="container-page">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-sm font-semibold uppercase tracking-wider text-accent-300 mb-3">
              Experience Summary
            </p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              Where We Deliver
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {summary.map((item) => (
              <div key={item.label} className="bg-white/5 border border-white/10 rounded-lg p-6 text-center">
                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <i className={`${item.icon} text-accent-300 text-xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-2 uppercase tracking-wide">
                  {item.label}
                </h3>
                <p className="text-sm text-foreground-300 leading-relaxed">
                  {item.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedContent
        subtitle="Related Information"
        title="Capabilities & Contracting Details"
        description="Connect proven experience to the capabilities Mitaja delivers and the procurement details government buyers need."
        links={[
          {
            label: "Explore Our Capabilities",
            description: "See the full range of enterprise IT, database, application, and platform capabilities Mitaja delivers.",
            href: "/capabilities",
            icon: "ri-apps-2-line",
          },
          {
            label: "Capability Statement",
            description: "Download or review Mitaja's capability statement for procurement and teaming review.",
            href: "/resources/capability-statement",
            icon: "ri-file-list-line",
          },
          {
            label: "Government Contracting Information",
            description: "UEI, CAGE, NAICS codes, GSA MAS, and state certifications for government procurement.",
            href: "/resources/contracting-information",
            icon: "ri-file-shield-2-line",
          },
        ]}
      />

      <CTAPanel
        title="Bring Proven Technology Experience to Your Next Mission"
        description="Mitaja combines U.S. government technology experience with international project delivery to support government organizations, prime contractors and enterprise partners across complex technology initiatives."
        secondaryAction={{ label: "Explore Capabilities", href: "/capabilities" }}
        variant="dark"
        children={
          <Link
            to="/contact"
            className="btn-primary bg-white text-primary-700 hover:bg-white/90 mt-0"
          >
            Contact Business Development
            <i className="ri-mail-line ml-2"></i>
          </Link>
        }
      />
    </Layout>
  );
}